import _ from 'lodash';
import { batch } from 'react-redux';

import { addFormHasFailedEndpoints, handleErrors } from '../../actions/ErrorActions';
import {
  getLinkPreviewResultsFailure,
  getLinkPreviewResultsSuccess,
  setLinkPreviewLoading,
} from '../../actions/LinkPreviewResultsActions';
import { fetchUserHasManageRights } from '../../actions/MasterSetupPageActions';
import { updateFullEntity } from '../../actions/OfflineDataActions';
import { getTaskStatusSuccess } from '../../actions/TaskStatusPanelActions';
import { addToast } from '../../actions/ToastActions';
import { addEntityResolverRules, addTranslations } from '../../actions/TranslationActions';
import { stopAutoChecking } from '../../actions/WorkflowActions';
import { getSimilarRecords } from '../../api/similar-records';
import { TOAST_TYPE } from '../../constants/NotificationConstants';
import { getLinkPreviewResultsKey } from '../../reducers/LinkPreviewResultsReducer';
import { checkTrue } from '../../utils/commonUtils';
import { isNilOrEmpty } from '../../utils/ramdaUtils';
import { UNIFIED_ID_PROPERTIES } from '../constants/editor.constants';
import {
  FORM_KEYS,
  FORM_MODEL_ATTRIBUTES,
  SS_CONTROL_TYPE,
} from '../constants/form-manager.constants';
import {
  DEFAULT_OPERATION_MESSAGE,
  RECORD_SIMILARITY_APP_SETTING_KEY,
} from '../constants/runtime.constants';
import {
  getEvaluatedConditions,
  getEvaluatedExpression,
} from '../selectors/form-manager.selectors';
import {
  getArrayOfPromisesForCreateCertificationEntity,
  getArrayOfPromisesForCreateDocumentEntity,
  getArrayOfPromisesForCreateEntity,
  getArrayOfPromisesForCreateFullEntity,
  getArrayOfPromisesForCreateImportEntity,
  getArrayOfPromisesForCreateNewEntity,
  getFormRules,
  mergeFormTranslations,
  preprocessDataSources,
  preprocessFormModel,
  preprocessFormModelForPreview,
  setFetchErrorProp,
} from '../utils/actions/form-manager.actions.utils';
import {
  createPrimaryKeysMap,
  createRelationShipKeysMap,
  findClosestParentIdByType,
  preprocessEntityData,
} from '../utils/form-manager.utils';
import { clearValidationStorage } from '../utils/validationStorage.utils';

export const LOAD_FORM = 'LOAD_FORM';
export const LOAD_DATA = 'LOAD_DATA';
export const UPDATE_FORM_MODEL = 'UPDATE_FORM_MODEL';
export const UPDATE_DELTA_CHANGES = 'UPDATE_DELTA_CHANGES';
export const UPDATE_LAYOUT_CHANGES = 'UPDATE_LAYOUT_CHANGES';
export const UPDATE_NEW_ELEMENTS_NODE = 'UPDATE_NEW_ELEMENTS_NODE';
export const CLEAR_FORM_DESIGNER = 'CLEAR_FORM_DESIGNER';
export const SET_SUPPORT_DATA = 'SET_SUPPORT_DATA';
export const SET_FORM_IMPORT_MODE = 'SET_FORM_IMPORT_MODE';
export const SAVE_SIMILAR_RECORDS_DATA = 'SAVE_SIMILAR_RECORDS_DATA';
export const SET_SIMILAR_RECORDS_DATA_FETCHED = 'SET_SIMILAR_RECORDS_DATA_FETCHED';

export const loadForm = (formKey, args) => ({
  type: LOAD_FORM,
  formKey,
  payload: {
    ...args,
  },
});

export const loadData = (formKey, args) => ({
  type: LOAD_DATA,
  formKey,
  payload: {
    ...args,
  },
});

export const setSupportData = (formKey, cachedSupportData, supportData) => ({
  type: SET_SUPPORT_DATA,
  formKey,
  payload: {
    cachedSupportData,
    supportData,
  },
});

export const updateFormModel = (formKey, newFormModel) => ({
  type: UPDATE_FORM_MODEL,
  formKey,
  payload: {
    newFormModel,
  },
});

export const setImportMode = (data) => ({
  type: SET_FORM_IMPORT_MODE,
  payload: data,
});

export const clearFormDesigner = (formKey) => ({
  type: CLEAR_FORM_DESIGNER,
  formKey,
});

const saveSimilarRecordsData = (similarRecordsData, formKey) => ({
  type: SAVE_SIMILAR_RECORDS_DATA,
  payload: { similarRecordsData },
  formKey,
});

const setSimilarRecordsDataFetched = () => ({
  type: SET_SIMILAR_RECORDS_DATA_FETCHED,
});

export const updateFormChanges = (formChanges, formKey) => {
  const changedControls = formChanges?.Properties?.Controls ?? [];

  const processedChangedControls = changedControls.length
    ? changedControls.map((item) => ({
        ...item,
        Properties: item.Properties.map((properties) => ({
          ...properties,
          name: UNIFIED_ID_PROPERTIES.includes(properties.name)
            ? 'uniqueIdentifier'
            : properties.name,
        })),
      }))
    : [];

  const deltaChanges = {
    controls:
      (formChanges && formChanges.deltaChanges) || (formChanges && processedChangedControls),
  };
  return {
    type: UPDATE_DELTA_CHANGES,
    formKey,
    payload: {
      deltaChanges,
    },
  };
};

export const updateLayoutChanges = ({ Layout }, formKey) => {
  return {
    type: UPDATE_LAYOUT_CHANGES,
    formKey,
    payload: {
      Layout,
    },
  };
};

export const updateNodeElementsInformation = ({ Layout }) => {
  const { AddedFields = [], DeletedFields = [], MovedFields = [] } = Layout || [];

  return {
    type: UPDATE_NEW_ELEMENTS_NODE,
    payload: {
      AddedFields,
      DeletedFields,
      MovedFields,
    },
  };
};

const loadSimilarRecords = (formModel, formKey, formId, itmId) => async (dispatch, getState) => {
  const similarRecordsControls = Object.values(formModel).filter(
    ({ controlType }) => controlType === SS_CONTROL_TYPE.AI_SIMILAR_RECORDS
  );

  if (!similarRecordsControls.length) {
    return;
  }

  const state = getState();

  const similarRecordsPayloads = similarRecordsControls.reduce((acc, control) => {
    const { controlId, sourceEntityName, searchName, dataObject } = control;

    const parentId = findClosestParentIdByType(formModel, controlId, SS_CONTROL_TYPE.SINGLE_TAB);
    const evaluatedSourceRecordId = getEvaluatedExpression(state, {
      id: controlId,
      prop: FORM_MODEL_ATTRIBUTES.SOURCE_RECORD_ID,
      formKey,
    });
    const showOperationCompleteMessage = getEvaluatedConditions(state, {
      id: controlId,
      prop: FORM_MODEL_ATTRIBUTES.SHOW_OPERATION_COMPLETED_MESSAGE,
      formKey,
    });
    const operationCompleteMessage = getEvaluatedExpression(state, {
      id: controlId,
      prop: FORM_MODEL_ATTRIBUTES.OPERATION_COMPLETED_MESSAGE,
      formKey,
    });
    const hidden = getEvaluatedConditions(state, {
      id: controlId,
      prop: FORM_MODEL_ATTRIBUTES.HIDDEN,
      formKey,
    });
    const isParentHidden = getEvaluatedConditions(state, {
      id: parentId,
      prop: FORM_MODEL_ATTRIBUTES.HIDDEN,
      formKey,
    });

    const navInfo = parentId
      ? {
          containerInfo: {
            containerId: parentId,
            controlType: SS_CONTROL_TYPE.SINGLE_TAB,
            formModel,
          },
          formKey,
        }
      : null;

    const isHidden = hidden || isParentHidden;

    if (!isHidden && evaluatedSourceRecordId && sourceEntityName) {
      acc.push({
        recordId: evaluatedSourceRecordId,
        entityId: sourceEntityName,
        searchName,
        searchEntityName: dataObject,
        showOperationCompleteMessage,
        controlId,
        navInfo,
        operationCompleteMessage,
      });
    }

    return acc;
  }, []);

  const similarRecordsResponses = await Promise.all(
    similarRecordsPayloads.map(({ recordId, entityId, searchName, searchEntityName }) =>
      getSimilarRecords({ recordId, entityId, searchName, searchEntityName })
    )
  );

  const similarRecordsDataToStore = similarRecordsResponses.reduce((acc, response = {}, index) => {
    if (response.hasError) {
      dispatch(handleErrors(response.errors));
      dispatch(setSimilarRecordsDataFetched());
      return acc;
    }

    const {
      controlId,
      showOperationCompleteMessage,
      navInfo,
      operationCompleteMessage,
    } = similarRecordsPayloads[index];
    const { columns = [], rows = [] } = response?.SearchData || {};

    acc = { ...acc, [controlId]: { columns, rows } };

    if (rows.length && showOperationCompleteMessage) {
      dispatch(
        addToast({
          message: operationCompleteMessage || DEFAULT_OPERATION_MESSAGE,
          type: TOAST_TYPE.AI,
          actionButtonText: "Don't show again",
          navInfo,
          skipToastKey: `${formId}_${itmId}`,
        })
      );
    }

    return acc;
  }, {});

  dispatch(saveSimilarRecordsData(similarRecordsDataToStore, formKey));
};

const handleEntityResponses = (
  formResponse,
  entityResponse,
  formDataResponse,
  formTranslationsResponse,
  params
) => async (dispatch, getState) => {
  const {
    entityViewName,
    entityType,
    orgId,
    formKey,
    containerWorkflowKey,
    workflowKey,
    workflowId,
    stepId,
    storeKey,
    itmId,
    parentWorkflowId,
    actionName,
    forEdit,
    certificationData,
    trainerSignOff,
  } = params;
  const appSettings = getState().appSettings || {};

  if (!formResponse.hasError && !entityResponse.hasError && !formDataResponse.hasError) {
    const formDefinition = formResponse.formDefinition;
    const { formModel, dataSources, formInfo } = formDefinition;
    const { entityDefinition, isMasterSetupEntity } = entityResponse;

    const workflowInfo = {
      entityViewName,
      entityType,
      orgId,
      formKey,
      containerWorkflowKey,
      workflowKey,
      workflowId,
      stepId,
      itmId,
      parentWorkflowId: parentWorkflowId ?? '',
      actionName: actionName ?? '',
      certificationData,
      trainerSignOff,
    };

    const dataLoadObj = {};

    const _entityName = Object.keys(entityDefinition)[0];
    const data = preprocessEntityData(formDataResponse, entityDefinition, _entityName);
    const originalEntityDataStore = data.originalEntityDataStore;
    delete data.originalEntityDataStore;

    // creating original data if empty
    let originalData;

    if (data.entityDataStore.entityData) {
      originalData = originalEntityDataStore
        ? { ...data, entityDataStore: originalEntityDataStore }
        : data;
    } else {
      originalData = {
        ...data,
        entityDataStore: {
          entityData: {
            [_entityName]: {},
          },
        },
      };
    }

    dataLoadObj.data = data;

    if (storeKey !== FORM_KEYS.DETAIL) {
      dataLoadObj.originalData = originalData;
    }

    if (!formTranslationsResponse.hasError) {
      dispatch(addTranslations(formTranslationsResponse.Translations));
      // I didn't want to use this, but there is no way to identify the resolver rules in the formModel right now
      dispatch(addEntityResolverRules(formTranslationsResponse.EntityResolverRules));
    } else {
      dispatch(handleErrors(formTranslationsResponse.errors));
    }

    const preprocessedFormModel = preprocessFormModel(formModel, storeKey);

    const loadObjs = {
      formModel: preprocessedFormModel,
      entityDefinition,
      dataSources: preprocessDataSources(dataSources),
      formInfo: { ...formInfo, isMasterSetupEntity },
      workflowInfo,
      formRules: getFormRules(preprocessedFormModel),
      primaryKeysMap: createPrimaryKeysMap(entityDefinition),
      relationShipKeysMap: createRelationShipKeysMap(entityDefinition),
    };

    const { formChanges } = formDefinition;

    const isSimilarRecordsServiceEnabled = checkTrue(
      appSettings[RECORD_SIMILARITY_APP_SETTING_KEY]
    );

    batch(() => {
      dispatch(loadForm(storeKey, { ...loadObjs }));
      dispatch(loadData(storeKey, { ...dataLoadObj }));
      dispatch(updateFormChanges(formChanges, storeKey));
      dispatch(updateLayoutChanges(formChanges, storeKey));

      if (forEdit) {
        dispatch(updateNodeElementsInformation(formChanges));
      }

      if (!forEdit && isSimilarRecordsServiceEnabled) {
        dispatch(loadSimilarRecords(preprocessedFormModel, storeKey, formInfo?.formKey, itmId));
      }
    });
  } else {
    const formResponseErrors = setFetchErrorProp(formResponse.errors);
    const entityResponseErrors = setFetchErrorProp(entityResponse.errors);
    const formDataResponseErrors = setFetchErrorProp(formDataResponse.errors);

    batch(() => {
      formResponse.hasError && dispatch(handleErrors(formResponseErrors));
      entityResponse.hasError && dispatch(handleErrors(entityResponseErrors));
      formDataResponse.hasError && dispatch(handleErrors(formDataResponseErrors));

      if ([FORM_KEYS.STEP, FORM_KEYS.DETAIL].includes(storeKey)) {
        dispatch(addFormHasFailedEndpoints({ storeKey }));
      }
    });
  }
};

const handleFullEntityFromLocalDB = (savedEntityData) => (dispatch) => {
  const entityData = savedEntityData.entity;

  const stepData = entityData.step;
  const detailData = entityData.detail;

  const translations = mergeFormTranslations(stepData.translations, detailData.translations);
  dispatch(addTranslations(translations));
  dispatch(addEntityResolverRules(stepData.entityResolverRules));

  const taskStatus = stepData.taskStatus;

  if (taskStatus) {
    dispatch(getTaskStatusSuccess(taskStatus));
  }

  const entityDefinition = stepData.entityDefinition;

  const stepLoadObjs = {
    formModel: stepData.formModel,
    entityDefinition,
    dataSources: stepData.dataSources,
    formInfo: stepData.formInfo,
    workflowInfo: stepData.workflowInfo,
    formRules: stepData.formRules,
    primaryKeysMap: createPrimaryKeysMap(entityDefinition),
    relationShipKeysMap: createRelationShipKeysMap(entityDefinition),
    cachedSupportData: stepData.cachedSupportData,
    supportData: stepData.supportData,
  };

  const detailLoadObjs = {
    formModel: detailData.formModel,
    entityDefinition,
    dataSources: detailData.dataSources,
    formInfo: detailData.formInfo,
    workflowInfo: detailData.workflowInfo,
    formRules: detailData.formRules,
    primaryKeysMap: createPrimaryKeysMap(entityDefinition),
    relationShipKeysMap: createRelationShipKeysMap(entityDefinition),
  };

  batch(() => {
    dispatch(loadForm(FORM_KEYS.STEP, { ...stepLoadObjs }));
    dispatch(
      loadData(FORM_KEYS.STEP, { data: stepData.data, originalData: stepData.originalData })
    );

    dispatch(loadForm(FORM_KEYS.DETAIL, { ...detailLoadObjs }));
    dispatch(loadData(FORM_KEYS.DETAIL, { data: detailData.data }));
  });
};

export const createEntity = ({
  entityViewName,
  entityType,
  orgId,
  formKey,
  formId,
  containerWorkflowKey,
  workflowKey,
  workflowId,
  stepId,
  storeKey,
  itmId,
  entityName,
  parentWorkflowId,
  entityTypeForDisplay,
  actionName,
  isCreateForm,
  newFormParams,
  isImportForm,
  formEntity,
  formDefinition,
}) => async (dispatch) => {
  // cleanup form
  clearValidationStorage();
  batch(() => {
    dispatch(clearFormDesigner(storeKey));
    dispatch(stopAutoChecking());
  });

  const forEdit = storeKey === FORM_KEYS.EDITOR;
  const isStepFormDefined = !_.isNil(stepId);

  const args = {
    formKey,
    formId,
    orgId,
    containerWorkflowKey,
    workflowKey,
    workflowId,
    stepId,
    forEdit,
    itmId,
    parentWorkflowId,
    entityName,
  };

  const [formResponse, entityResponse, formDataResponse, formTranslationsResponse] = isImportForm
    ? await Promise.all(
        getArrayOfPromisesForCreateImportEntity({
          ...args,
          formEntity,
        })
      )
    : isCreateForm
    ? await Promise.all(
        getArrayOfPromisesForCreateNewEntity({
          ...args,
          isCreateForm,
          newFormParams,
        })
      )
    : await Promise.all(
        getArrayOfPromisesForCreateEntity({
          ...args,
          isStepFormDefined,
          entityTypeForDisplay,
          entityType,
        })
      );

  if (isImportForm) {
    formResponse.formDefinition = formDefinition;
  }

  if (entityResponse.isMasterSetupEntity) {
    await dispatch(fetchUserHasManageRights(entityName || entityType));
  }

  dispatch(
    handleEntityResponses(
      formResponse,
      entityResponse,
      formDataResponse,
      formTranslationsResponse,
      {
        entityViewName,
        entityType,
        orgId,
        formKey,
        containerWorkflowKey,
        workflowKey,
        workflowId,
        stepId,
        storeKey,
        itmId,
        parentWorkflowId,
        actionName,
        forEdit,
      }
    )
  );
};

/**
 * Certification page only
 */
export const createCertificationEntity = ({
  storeKey,
  formEntityKey,
  formKey,
  itmId,
  stepId,
  entityType,
  certificationData = true,
  trainerSignOff = false,
}) => async (dispatch) => {
  // cleanup form
  clearValidationStorage();
  batch(() => {
    dispatch(clearFormDesigner(storeKey));
    dispatch(stopAutoChecking());
  });

  const [
    formResponse,
    entityResponse,
    formDataResponse,
    formTranslationsResponse,
  ] = await Promise.all(
    getArrayOfPromisesForCreateCertificationEntity({
      formEntityKey,
      formKey,
      itmId,
      stepId,
      isDetail: storeKey === FORM_KEYS.DETAIL,
      entityType,
      certificationData,
      trainerSignOff,
    })
  );

  dispatch(
    handleEntityResponses(
      formResponse,
      entityResponse,
      formDataResponse,
      formTranslationsResponse,
      {
        entityType,
        formKey,
        stepId,
        storeKey,
        itmId,
        certificationData,
        trainerSignOff,
      }
    )
  );
};

/**
 * Document viewer page only
 */
export const createDocumentEntity = ({ entityType, formKey, itmId, certificationData }) => async (
  dispatch
) => {
  const storeKey = FORM_KEYS.DETAIL;

  // cleanup form
  clearValidationStorage();
  batch(() => {
    dispatch(clearFormDesigner(storeKey));
    dispatch(stopAutoChecking());
  });

  const [
    formResponse,
    entityResponse,
    formDataResponse,
    formTranslationsResponse,
  ] = await Promise.all(
    getArrayOfPromisesForCreateDocumentEntity({
      entityType,
      formKey,
      itmId,
      certificationData,
    })
  );

  dispatch(
    handleEntityResponses(
      formResponse,
      entityResponse,
      formDataResponse,
      formTranslationsResponse,
      {
        entityType,
        formKey,
        itmId,
        certificationData,
        storeKey,
      }
    )
  );
};

export const createFullEntityOffline = (savedEntityData) => (dispatch) => {
  clearValidationStorage();
  batch(() => {
    dispatch(clearFormDesigner(FORM_KEYS.STEP));
    dispatch(clearFormDesigner(FORM_KEYS.DETAIL));
    dispatch(stopAutoChecking());
    dispatch(handleFullEntityFromLocalDB(savedEntityData));
  });
};

/**
 * Mount both step and detail forms
 */
export const createFullEntity = ({
  entityViewName,
  entityType,
  orgId,
  formKey,
  containerWorkflowKey,
  workflowKey,
  workflowId,
  stepId,
  itmId,
  entityName,
  parentWorkflowId,
}) => async (dispatch, getState) => {
  clearValidationStorage();
  batch(() => {
    dispatch(clearFormDesigner(FORM_KEYS.STEP));
    dispatch(clearFormDesigner(FORM_KEYS.DETAIL));
    dispatch(stopAutoChecking());
  });

  const state = getState();
  const entityId = `${itmId}_${stepId}`;
  const appSettings = state.appSettings || {};
  const savedEntityData = state.offlineData.entities?.find((entity) => entity.id === entityId);
  const clientChanges = savedEntityData?.requestChanges;
  const hasUnsynchedChanges = !isNilOrEmpty(clientChanges);
  const savedEntityExists = !isNilOrEmpty(savedEntityData);
  const cachedSupportData = savedEntityData?.step?.cachedSupportData;

  const [
    stepFormResponse,
    detailFormResponse,
    entityResponse,
    formDataResponse,
    stepFormTranslationsResponse,
    detailFormTranslationsResponse,
  ] = await Promise.all(
    getArrayOfPromisesForCreateFullEntity({
      orgId,
      containerWorkflowKey,
      workflowKey,
      workflowId,
      stepId,
      formKey,
      entityType,
      itmId,
      parentWorkflowId,
      entityName,
    })
  );

  if (
    !stepFormResponse.hasError &&
    !detailFormResponse.hasError &&
    !entityResponse.hasError &&
    !formDataResponse.hasError
  ) {
    const stepFormDefinition = stepFormResponse.formDefinition;
    const detailFormDefinition = detailFormResponse.formDefinition;
    const { entityDefinition } = entityResponse;

    const workflowInfo = {
      entityViewName,
      entityType,
      orgId,
      formKey,
      containerWorkflowKey,
      workflowKey,
      workflowId,
      stepId,
      itmId,
      parentWorkflowId: parentWorkflowId ?? '',
    };

    const dataLoadObj = {};

    const _entityName = Object.keys(entityDefinition)[0];
    const data = preprocessEntityData(formDataResponse, entityDefinition, _entityName);
    const originalEntityDataStore = data.originalEntityDataStore;
    delete data.originalEntityDataStore;

    // creating original data if empty
    let originalData;

    if (data.entityDataStore.entityData) {
      originalData = originalEntityDataStore
        ? { ...data, entityDataStore: originalEntityDataStore }
        : data;
    } else {
      originalData = {
        ...data,
        entityDataStore: {
          entityData: {
            [_entityName]: {},
          },
        },
      };
    }

    dataLoadObj.data = data;

    if (!stepFormTranslationsResponse.hasError && !detailFormTranslationsResponse.hasError) {
      const translations = mergeFormTranslations(
        stepFormTranslationsResponse.Translations,
        detailFormTranslationsResponse.Translations
      );
      dispatch(addTranslations(translations));
      dispatch(addEntityResolverRules(stepFormTranslationsResponse.EntityResolverRules));
    } else {
      dispatch(handleErrors(stepFormTranslationsResponse.errors));
      dispatch(handleErrors(detailFormTranslationsResponse.errors));
    }

    const preprocessedStepFormModel = preprocessFormModel(stepFormDefinition.formModel);
    const preprocessedDetailFormModel = preprocessFormModel(detailFormDefinition.formModel);

    const stepLoadObjs = {
      formModel: preprocessedStepFormModel,
      entityDefinition,
      dataSources: preprocessDataSources(stepFormDefinition.dataSources),
      formInfo: { ...stepFormDefinition.formInfo },
      workflowInfo,
      formRules: getFormRules(preprocessedStepFormModel),
      primaryKeysMap: createPrimaryKeysMap(entityDefinition),
      relationShipKeysMap: createRelationShipKeysMap(entityDefinition),
      cachedSupportData,
    };

    const detailLoadObjs = {
      formModel: preprocessedDetailFormModel,
      entityDefinition,
      dataSources: preprocessDataSources(detailFormDefinition.dataSources),
      formInfo: { ...detailFormDefinition.formInfo },
      workflowInfo,
      formRules: getFormRules(preprocessedDetailFormModel),
      primaryKeysMap: createPrimaryKeysMap(entityDefinition),
      relationShipKeysMap: createRelationShipKeysMap(entityDefinition),
    };

    if (!hasUnsynchedChanges && savedEntityExists) {
      const stepEntity = _.omit(stepLoadObjs, ['primaryKeysMap', 'relationShipKeysMap']);
      const detailEntity = _.omit(detailLoadObjs, ['primaryKeysMap', 'relationShipKeysMap']);

      dispatch(
        updateFullEntity(
          entityId,
          {
            ...stepEntity,
            data: dataLoadObj.data,
            originalData,
            entityResolverRules: stepFormTranslationsResponse.EntityResolverRules,
            translations: stepFormTranslationsResponse.Translations,
          },
          {
            ...detailEntity,
            data: dataLoadObj.data,
            translations: detailFormTranslationsResponse.Translations,
          }
        )
      );
    }

    const isSimilarRecordsServiceEnabled = checkTrue(
      appSettings[RECORD_SIMILARITY_APP_SETTING_KEY]
    );

    batch(() => {
      dispatch(loadForm(FORM_KEYS.STEP, { ...stepLoadObjs }));
      dispatch(loadData(FORM_KEYS.STEP, { ...dataLoadObj, originalData }));
      dispatch(updateFormChanges(stepFormDefinition.formChanges, FORM_KEYS.STEP));
      dispatch(updateLayoutChanges(stepFormDefinition.formChanges, FORM_KEYS.STEP));

      dispatch(loadForm(FORM_KEYS.DETAIL, { ...detailLoadObjs }));
      dispatch(loadData(FORM_KEYS.DETAIL, { ...dataLoadObj }));
      dispatch(updateFormChanges(detailFormDefinition.formChanges, FORM_KEYS.DETAIL));
      dispatch(updateLayoutChanges(detailFormDefinition.formChanges, FORM_KEYS.DETAIL));

      if (isSimilarRecordsServiceEnabled) {
        dispatch(
          loadSimilarRecords(
            preprocessedStepFormModel,
            FORM_KEYS.STEP,
            stepFormDefinition.formInfo?.formKey,
            itmId
          )
        );
        dispatch(
          loadSimilarRecords(
            preprocessedDetailFormModel,
            FORM_KEYS.DETAIL,
            detailFormDefinition.formInfo?.formKey,
            itmId
          )
        );
      }
    });
  } else {
    batch(() => {
      stepFormResponse.hasError && dispatch(handleErrors(stepFormResponse.errors));
      detailFormResponse.hasError && dispatch(handleErrors(detailFormResponse.errors));
      entityResponse.hasError && dispatch(handleErrors(entityResponse.errors));
      formDataResponse.hasError && dispatch(handleErrors(formDataResponse.errors));

      const storeKeyWithFailedEndpoints =
        stepFormResponse.hasError || entityResponse.hasError || formDataResponse.hasError
          ? FORM_KEYS.STEP
          : FORM_KEYS.DETAIL;

      dispatch(addFormHasFailedEndpoints({ storeKey: storeKeyWithFailedEndpoints }));
    });
  }
};

export const createPreview = ({
  entityType,
  formKey,
  formId,
  containerWorkflowKey,
  workflowKey,
  workflowId,
  stepId,
  itmId,
  entityName,
  parentWorkflowId,
}) => async (dispatch, getState) => {
  const state = getState();

  const linkPreviewResultsKey = getLinkPreviewResultsKey({ formKey, id: itmId });
  const cachedDetails = state.linkPreviewResults[linkPreviewResultsKey] ?? {};
  const { cachedFormKey: storedCacheFormKey } = state.formDesigner[FORM_KEYS.LINK_PREVIEW] ?? {};

  if (
    cachedDetails.isLoading ||
    (storedCacheFormKey && storedCacheFormKey === linkPreviewResultsKey)
  ) {
    return;
  }

  if (cachedDetails.formData && cachedDetails.entityData) {
    batch(() => {
      dispatch(loadForm(FORM_KEYS.LINK_PREVIEW, { ...cachedDetails.formData }));
      dispatch(loadData(FORM_KEYS.LINK_PREVIEW, { ...cachedDetails.entityData }));
    });
    return;
  }

  const args = {
    formKey,
    formId,
    containerWorkflowKey,
    workflowKey,
    workflowId,
    stepId,
    itmId,
    parentWorkflowId,
    entityName,
  };

  dispatch(setLinkPreviewLoading({ formKey, id: itmId }));

  const [
    formResponse,
    entityResponse,
    formDataResponse,
    formTranslationsResponse,
  ] = await Promise.all(
    getArrayOfPromisesForCreateEntity({
      ...args,
      isStepFormDefined: false,
      entityType,
    })
  );

  dispatch(
    handlePreviewResponses(
      formResponse,
      entityResponse,
      formDataResponse,
      formTranslationsResponse,
      formKey,
      itmId
    )
  );
};

const handlePreviewResponses = (
  formResponse,
  entityResponse,
  formDataResponse,
  formTranslationsResponse,
  formKey,
  itmId
) => async (dispatch) => {
  if (!formResponse.hasError && !entityResponse.hasError && !formDataResponse.hasError) {
    const formDefinition = formResponse.formDefinition;
    const { formModel, formInfo } = formDefinition;
    const { entityDefinition } = entityResponse;

    const dataLoadObj = {};

    const _entityName = Object.keys(entityDefinition)[0];
    const data = preprocessEntityData(formDataResponse, entityDefinition, _entityName);
    delete data.originalEntityDataStore;

    dataLoadObj.data = data;

    const preprocessedFormModel = preprocessFormModelForPreview(formModel);

    const loadObjs = {
      cachedFormKey: getLinkPreviewResultsKey({ formKey, id: itmId }),
      formModel: preprocessedFormModel,
      entityDefinition,
      formInfo,
    };

    batch(() => {
      dispatch(loadForm(FORM_KEYS.LINK_PREVIEW, { ...loadObjs }));
      dispatch(loadData(FORM_KEYS.LINK_PREVIEW, { ...dataLoadObj }));

      if (!formTranslationsResponse.hasError) {
        dispatch(addTranslations(formTranslationsResponse.Translations));
      } else {
        dispatch(handleErrors(formTranslationsResponse.errors));
      }

      dispatch(
        getLinkPreviewResultsSuccess({
          formKey,
          id: itmId,
          formData: { ...loadObjs },
          entityData: { ...dataLoadObj },
        })
      );
    });
  } else {
    const errors =
      (formResponse.hasError && formResponse.errors) ||
      (entityResponse.hasError && entityResponse.errors) ||
      (formDataResponse.hasError && formDataResponse.errors);

    dispatch(
      getLinkPreviewResultsFailure({
        formKey,
        id: itmId,
        errors,
      })
    );
  }
};
